import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  Settings,
  FolderKanban,
  MessageSquare,
  Bell,
  LogOut,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Loader2,
  Building2,
  Mail,
  Phone,
  Calendar,
  FileText,
  ExternalLink,
  Plus,
  Receipt,
  MapPin,
  Hash,
  UserCircle,
  Link2,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';

const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
import { logOut, updateUserProfile } from '@/services/auth';
import { doc, getDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { getFirebaseFirestore } from '@/services/firebase/config';
import { ConnectionsPage, CompanyProfilePage, AIChatPage, AnalyticsDashboard, NotificationsPage, ReportsPage, PaywallModal, ProtectedContent } from '@/modules/ecom-intelligence';
import { useSubscription } from '@/context/SubscriptionContext';
import { Brain, MessageSquareText, BarChart3, BellRing, Sparkles } from 'lucide-react';

interface Project {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  type: string;
  createdAt: Date;
  updatedAt: Date;
}

interface Message {
  id: string;
  subject: string;
  preview: string;
  read: boolean;
  createdAt: Date;
}

interface UserProfile {
  displayName: string;
  email: string;
  phone?: string;
  company?: string;
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  createdAt: Date;
}

interface BillingInfo {
  billingType: 'corporate' | 'individual';
  // Kurumsal (Corporate)
  companyName?: string;
  taxNumber?: string;
  taxOffice?: string;
  // Şahıs (Individual)
  identityNumber?: string;
  fullName?: string;
  // Ortak alanlar
  address?: string;
  city?: string;
  district?: string;
  postalCode?: string;
  country?: string;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const {
    hasAccess,
    isTrialActive,
    isTrialExpired,
    daysRemaining,
    showPaywall,
    setShowPaywall,
    servicesActive
  } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { id: 'overview', label: t('dashboard.tabs.overview'), icon: User },
    { id: 'analytics', label: t('dashboard.tabs.analytics', 'Analitik'), icon: BarChart3 },
    { id: 'notifications', label: t('dashboard.tabs.notifications', 'Bildirimler'), icon: BellRing },
    { id: 'reports', label: t('dashboard.tabs.reports', 'Raporlar'), icon: FileText },
    { id: 'connections', label: t('dashboard.tabs.connections', 'Bağlantılar'), icon: Link2 },
    { id: 'ai-profile', label: t('dashboard.tabs.aiProfile', 'AI Profil'), icon: Brain },
    { id: 'ai-chat', label: t('dashboard.tabs.aiChat', 'AI Asistan'), icon: MessageSquareText },
    { id: 'projects', label: t('dashboard.tabs.projects'), icon: FolderKanban },
    { id: 'messages', label: t('dashboard.tabs.messages'), icon: MessageSquare },
    { id: 'settings', label: t('dashboard.tabs.settings'), icon: Settings },
  ];

  // Get initial tab from URL path (e.g., /dashboard/connections -> connections)
  const getTabFromPath = () => {
    const pathParts = location.pathname.split('/');
    const tabFromPath = pathParts[pathParts.length - 1];
    const validTabs = tabs.map(t => t.id);
    return validTabs.includes(tabFromPath) ? tabFromPath : 'overview';
  };

  const [activeTab, setActiveTab] = useState(getTabFromPath);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    phone: '',
    company: '',
  });
  const [billingForm, setBillingForm] = useState<BillingInfo>({
    billingType: 'corporate',
    companyName: '',
    taxNumber: '',
    taxOffice: '',
    identityNumber: '',
    fullName: '',
    address: '',
    city: '',
    district: '',
    postalCode: '',
    country: 'Türkiye',
  });
  const [savingBilling, setSavingBilling] = useState(false);

  // Sync active tab with URL path changes
  useEffect(() => {
    const tabFromPath = getTabFromPath();
    if (tabFromPath !== activeTab) {
      setActiveTab(tabFromPath);
    }
  }, [location.pathname]);

  // Fetch user data from Firestore
  useEffect(() => {
    async function fetchUserData() {
      if (!user) return;

      try {
        const db = getFirebaseFirestore();

        // Fetch user profile
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserProfile({
            displayName: data.displayName || user.displayName || '',
            email: data.email || user.email || '',
            phone: data.phone || '',
            company: data.company || '',
            plan: data.plan || 'free',
            createdAt: data.createdAt?.toDate() || new Date(),
          });
          setProfileForm({
            displayName: data.displayName || user.displayName || '',
            phone: data.phone || '',
            company: data.company || '',
          });
          // Load billing info if exists
          if (data.billing) {
            setBillingForm({
              billingType: data.billing.billingType || 'corporate',
              companyName: data.billing.companyName || '',
              taxNumber: data.billing.taxNumber || '',
              taxOffice: data.billing.taxOffice || '',
              identityNumber: data.billing.identityNumber || '',
              fullName: data.billing.fullName || '',
              address: data.billing.address || '',
              city: data.billing.city || '',
              district: data.billing.district || '',
              postalCode: data.billing.postalCode || '',
              country: data.billing.country || 'Türkiye',
            });
          }
        }

        // Fetch projects
        try {
          const projectsQuery = query(
            collection(db, 'projects'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc'),
            limit(5)
          );
          const projectsSnapshot = await getDocs(projectsQuery);
          const projectsData = projectsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          })) as Project[];
          setProjects(projectsData);
        } catch {
          // Projects collection might not exist yet
          setProjects([]);
        }

        // Fetch messages
        try {
          const messagesQuery = query(
            collection(db, 'messages'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc'),
            limit(5)
          );
          const messagesSnapshot = await getDocs(messagesQuery);
          const messagesData = messagesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
          })) as Message[];
          setMessages(messagesData);
        } catch {
          // Messages collection might not exist yet
          setMessages([]);
        }

      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      fetchUserData();
    }
  }, [user]);

  // Update form when user data loads
  useEffect(() => {
    if (user && !userProfile) {
      setProfileForm({
        displayName: user.displayName || '',
        phone: '',
        company: '',
      });
    }
    // Reset image error when user changes
    setImageError(false);
  }, [user, userProfile]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/', { state: { requireAuth: true } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Show loading while checking auth
  if (authLoading || loading) {
    return (
      <div className="pt-24 min-h-screen bg-bg-base flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4 text-accent" size={48} />
          <p className="text-text-secondary">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  const handleSignOut = async () => {
    try {
      await logOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.displayName.trim()) return;

    setSaving(true);
    try {
      await updateUserProfile({ displayName: profileForm.displayName });
      // Update Firestore with additional fields
      const db = getFirebaseFirestore();
      const { setDoc } = await import('firebase/firestore');
      await setDoc(doc(db, 'users', user.uid), {
        displayName: profileForm.displayName,
        phone: profileForm.phone,
        company: profileForm.company,
        updatedAt: new Date(),
      }, { merge: true });
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBilling = async () => {
    setSavingBilling(true);
    try {
      const db = getFirebaseFirestore();
      const { setDoc } = await import('firebase/firestore');
      await setDoc(doc(db, 'users', user.uid), {
        billing: {
          billingType: billingForm.billingType,
          companyName: billingForm.companyName,
          taxNumber: billingForm.taxNumber,
          taxOffice: billingForm.taxOffice,
          identityNumber: billingForm.identityNumber,
          fullName: billingForm.fullName,
          address: billingForm.address,
          city: billingForm.city,
          district: billingForm.district,
          postalCode: billingForm.postalCode,
          country: billingForm.country,
          updatedAt: new Date(),
        },
        updatedAt: new Date(),
      }, { merge: true });
    } catch (error) {
      console.error('Failed to update billing info:', error);
    } finally {
      setSavingBilling(false);
    }
  };

  const getInitials = (name: string | null, email: string | null) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={16} className="text-green-500" />;
      case 'in_progress':
        return <Clock size={16} className="text-blue-500" />;
      case 'pending':
        return <AlertCircle size={16} className="text-yellow-500" />;
      default:
        return <AlertCircle size={16} className="text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return t('dashboard.status.completed');
      case 'in_progress':
        return t('dashboard.status.inProgress');
      case 'pending':
        return t('dashboard.status.pending');
      case 'cancelled':
        return t('dashboard.status.cancelled');
      default:
        return status;
    }
  };

  const getPlanBadge = (plan: string) => {
    const colors: Record<string, string> = {
      free: 'bg-gray-500/20 text-gray-400',
      starter: 'bg-blue-500/20 text-blue-400',
      professional: 'bg-purple-500/20 text-purple-400',
      enterprise: 'bg-amber-500/20 text-amber-400',
    };
    return colors[plan] || colors.free;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Welcome Card */}
            <div className="card bg-gradient-to-br from-accent/10 to-purple-500/5 border-accent/20">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold mb-2">
                    {t('dashboard.welcome')}, {user.displayName || t('dashboard.user')}! 👋
                  </h2>
                  <p className="text-text-secondary">
                    {t('dashboard.welcomeMessage')}
                  </p>
                </div>
                <Link to="/contact" className="btn btn-primary btn-sm">
                  <Plus size={16} />
                  {t('dashboard.newProject')}
                </Link>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <FolderKanban size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{projects.length}</div>
                    <div className="text-text-secondary text-sm">{t('dashboard.totalProjects')}</div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 size={20} className="text-green-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {projects.filter(p => p.status === 'completed').length}
                    </div>
                    <div className="text-text-secondary text-sm">{t('dashboard.completedProjects')}</div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <MessageSquare size={20} className="text-purple-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {messages.filter(m => !m.read).length}
                    </div>
                    <div className="text-text-secondary text-sm">{t('dashboard.unreadMessages')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Projects */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">{t('dashboard.recentProjects')}</h3>
                <Link
                  to="/dashboard/projects"
                  className="text-accent text-sm hover:underline"
                >
                  {t('dashboard.viewAll')}
                </Link>
              </div>

              {projects.length > 0 ? (
                <div className="space-y-3">
                  {projects.slice(0, 3).map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-bg-elevated hover:bg-bg-surface transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {getStatusIcon(project.status)}
                        <div>
                          <div className="font-medium">{project.name}</div>
                          <div className="text-text-tertiary text-sm">{project.type}</div>
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-text-tertiary" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FolderKanban size={48} className="mx-auto mb-4 text-text-tertiary opacity-50" />
                  <p className="text-text-secondary mb-4">{t('dashboard.noProjects')}</p>
                  <Link to="/contact" className="btn btn-primary btn-sm">
                    {t('dashboard.startProject')}
                  </Link>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.quickLinks')}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Link to="/services" className="flex items-center gap-3 p-3 rounded-lg bg-bg-elevated hover:bg-bg-surface transition-colors">
                  <FileText size={18} className="text-accent" />
                  <span>{t('dashboard.ourServices')}</span>
                  <ExternalLink size={14} className="text-text-tertiary ml-auto" />
                </Link>
                <Link to="/products" className="flex items-center gap-3 p-3 rounded-lg bg-bg-elevated hover:bg-bg-surface transition-colors">
                  <FolderKanban size={18} className="text-accent" />
                  <span>{t('dashboard.ourProducts')}</span>
                  <ExternalLink size={14} className="text-text-tertiary ml-auto" />
                </Link>
                <Link to="/blog" className="flex items-center gap-3 p-3 rounded-lg bg-bg-elevated hover:bg-bg-surface transition-colors">
                  <FileText size={18} className="text-accent" />
                  <span>{t('dashboard.blog')}</span>
                  <ExternalLink size={14} className="text-text-tertiary ml-auto" />
                </Link>
                <Link to="/contact" className="flex items-center gap-3 p-3 rounded-lg bg-bg-elevated hover:bg-bg-surface transition-colors">
                  <MessageSquare size={18} className="text-accent" />
                  <span>{t('dashboard.contactUs')}</span>
                  <ExternalLink size={14} className="text-text-tertiary ml-auto" />
                </Link>
              </div>
            </div>
          </div>
        );

      case 'projects':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">{t('dashboard.allProjects')}</h2>
              <Link to="/contact" className="btn btn-primary btn-sm">
                <Plus size={16} />
                {t('dashboard.requestProject')}
              </Link>
            </div>

            {projects.length > 0 ? (
              <div className="space-y-4">
                {projects.map((project) => (
                  <div key={project.id} className="card hover:border-accent/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {getStatusIcon(project.status)}
                        <div>
                          <h3 className="font-semibold">{project.name}</h3>
                          <p className="text-text-secondary text-sm">{project.type}</p>
                          <div className="flex items-center gap-4 mt-2 text-text-tertiary text-sm">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {project.createdAt.toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span className={cn(
                        'px-3 py-1 rounded-full text-xs font-medium',
                        project.status === 'completed' && 'bg-green-500/20 text-green-400',
                        project.status === 'in_progress' && 'bg-blue-500/20 text-blue-400',
                        project.status === 'pending' && 'bg-yellow-500/20 text-yellow-400',
                        project.status === 'cancelled' && 'bg-red-500/20 text-red-400',
                      )}>
                        {getStatusText(project.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card text-center py-12">
                <FolderKanban size={64} className="mx-auto mb-4 text-text-tertiary opacity-50" />
                <h3 className="text-lg font-semibold mb-2">{t('dashboard.noProjectsYet')}</h3>
                <p className="text-text-secondary mb-6">{t('dashboard.noProjectsDesc')}</p>
                <Link to="/contact" className="btn btn-primary">
                  {t('dashboard.startFirstProject')}
                </Link>
              </div>
            )}
          </div>
        );

      case 'messages':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">{t('dashboard.allMessages')}</h2>

            {messages.length > 0 ? (
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      'card hover:border-accent/30 transition-colors cursor-pointer',
                      !message.read && 'border-accent/20 bg-accent/5'
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {!message.read && (
                          <div className="w-2 h-2 rounded-full bg-accent mt-2" />
                        )}
                        <div>
                          <h3 className="font-semibold">{message.subject}</h3>
                          <p className="text-text-secondary text-sm line-clamp-2">{message.preview}</p>
                          <span className="text-text-tertiary text-xs mt-2 block">
                            {message.createdAt.toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-text-tertiary" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card text-center py-12">
                <MessageSquare size={64} className="mx-auto mb-4 text-text-tertiary opacity-50" />
                <h3 className="text-lg font-semibold mb-2">{t('dashboard.noMessages')}</h3>
                <p className="text-text-secondary">{t('dashboard.noMessagesDesc')}</p>
              </div>
            )}
          </div>
        );

      case 'analytics':
        return (
          <ProtectedContent
            hasAccess={hasAccess}
            featureName="Analitik Dashboard"
            onUpgrade={() => setShowPaywall(true)}
          >
            <AnalyticsDashboard />
          </ProtectedContent>
        );

      case 'notifications':
        return (
          <ProtectedContent
            hasAccess={hasAccess}
            featureName="Bildirim Sistemi"
            onUpgrade={() => setShowPaywall(true)}
          >
            <NotificationsPage />
          </ProtectedContent>
        );

      case 'reports':
        return (
          <ProtectedContent
            hasAccess={hasAccess}
            featureName="Raporlama Modülü"
            onUpgrade={() => setShowPaywall(true)}
          >
            <ReportsPage />
          </ProtectedContent>
        );

      case 'connections':
        return (
          <ProtectedContent
            hasAccess={hasAccess}
            featureName="Platform Bağlantıları"
            onUpgrade={() => setShowPaywall(true)}
          >
            <ConnectionsPage />
          </ProtectedContent>
        );

      case 'ai-profile':
        return (
          <ProtectedContent
            hasAccess={hasAccess}
            featureName="AI Profil"
            onUpgrade={() => setShowPaywall(true)}
          >
            <CompanyProfilePage />
          </ProtectedContent>
        );

      case 'ai-chat':
        return (
          <ProtectedContent
            hasAccess={hasAccess}
            featureName="AI Asistan"
            onUpgrade={() => setShowPaywall(true)}
          >
            <AIChatPage />
          </ProtectedContent>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <div className="card">
              <h3 className="font-semibold mb-6">{t('dashboard.profileSettings')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">
                    {t('dashboard.displayName')}
                  </label>
                  <input
                    type="text"
                    value={profileForm.displayName}
                    onChange={(e) => setProfileForm({ ...profileForm, displayName: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-text-secondary text-sm mb-2">
                    {t('dashboard.email')}
                  </label>
                  <input
                    type="email"
                    value={user.email || ''}
                    disabled
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default text-text-tertiary cursor-not-allowed"
                  />
                  <p className="text-text-tertiary text-xs mt-1">{t('dashboard.emailCannotChange')}</p>
                </div>

                <div>
                  <label className="block text-text-secondary text-sm mb-2">
                    {t('dashboard.phone')}
                  </label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    placeholder="+90 555 123 4567"
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>

                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      {t('dashboard.saving')}
                    </>
                  ) : (
                    t('dashboard.saveChanges')
                  )}
                </button>
              </div>
            </div>

            {/* Fatura Bilgileri */}
            <div className="card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                  <Receipt size={20} className="text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold">{t('dashboard.billingInfo')}</h3>
                  <p className="text-text-secondary text-sm">{t('dashboard.billingInfoDesc')}</p>
                </div>
              </div>

              {/* Fatura Tipi Seçimi */}
              <div className="mb-6">
                <label className="block text-text-secondary text-sm mb-3">
                  {t('dashboard.billingType')}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setBillingForm({ ...billingForm, billingType: 'corporate' })}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all',
                      billingForm.billingType === 'corporate'
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border-default hover:border-accent/50 text-text-secondary'
                    )}
                  >
                    <Building2 size={18} />
                    {t('dashboard.corporate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingForm({ ...billingForm, billingType: 'individual' })}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all',
                      billingForm.billingType === 'individual'
                        ? 'border-accent bg-accent/10 text-accent'
                        : 'border-border-default hover:border-accent/50 text-text-secondary'
                    )}
                  >
                    <UserCircle size={18} />
                    {t('dashboard.individual')}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {/* Kurumsal Alanlar */}
                {billingForm.billingType === 'corporate' && (
                  <>
                    <div>
                      <label className="block text-text-secondary text-sm mb-2">
                        <Building2 size={14} className="inline mr-1" />
                        {t('dashboard.companyNameBilling')}
                      </label>
                      <input
                        type="text"
                        value={billingForm.companyName}
                        onChange={(e) => setBillingForm({ ...billingForm, companyName: e.target.value })}
                        placeholder="Örn: Bosphorus Labs Teknoloji A.Ş."
                        className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-text-secondary text-sm mb-2">
                          <Hash size={14} className="inline mr-1" />
                          {t('dashboard.taxNumber')}
                        </label>
                        <input
                          type="text"
                          value={billingForm.taxNumber}
                          onChange={(e) => setBillingForm({ ...billingForm, taxNumber: e.target.value })}
                          placeholder="1234567890"
                          maxLength={11}
                          className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-text-secondary text-sm mb-2">
                          <Building2 size={14} className="inline mr-1" />
                          {t('dashboard.taxOffice')}
                        </label>
                        <input
                          type="text"
                          value={billingForm.taxOffice}
                          onChange={(e) => setBillingForm({ ...billingForm, taxOffice: e.target.value })}
                          placeholder="Örn: Beşiktaş"
                          className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Şahıs Alanları */}
                {billingForm.billingType === 'individual' && (
                  <>
                    <div>
                      <label className="block text-text-secondary text-sm mb-2">
                        <UserCircle size={14} className="inline mr-1" />
                        {t('dashboard.fullName')}
                      </label>
                      <input
                        type="text"
                        value={billingForm.fullName}
                        onChange={(e) => setBillingForm({ ...billingForm, fullName: e.target.value })}
                        placeholder="Ad Soyad"
                        className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-text-secondary text-sm mb-2">
                        <Hash size={14} className="inline mr-1" />
                        {t('dashboard.identityNumber')}
                      </label>
                      <input
                        type="text"
                        value={billingForm.identityNumber}
                        onChange={(e) => setBillingForm({ ...billingForm, identityNumber: e.target.value })}
                        placeholder="TC Kimlik No (11 haneli)"
                        maxLength={11}
                        className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                      />
                    </div>
                  </>
                )}

                {/* Ortak Adres Alanları */}
                <div className="pt-4 border-t border-border-default">
                  <h4 className="font-medium text-sm mb-4 flex items-center gap-2">
                    <MapPin size={16} className="text-accent" />
                    {t('dashboard.addressInfo')}
                  </h4>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-text-secondary text-sm mb-2">
                        {t('dashboard.address')}
                      </label>
                      <textarea
                        value={billingForm.address}
                        onChange={(e) => setBillingForm({ ...billingForm, address: e.target.value })}
                        placeholder="Mahalle, Cadde/Sokak, Bina No, Daire No"
                        rows={2}
                        className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none"
                      />
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-text-secondary text-sm mb-2">
                          {t('dashboard.city')}
                        </label>
                        <input
                          type="text"
                          value={billingForm.city}
                          onChange={(e) => setBillingForm({ ...billingForm, city: e.target.value })}
                          placeholder="İstanbul"
                          className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-text-secondary text-sm mb-2">
                          {t('dashboard.district')}
                        </label>
                        <input
                          type="text"
                          value={billingForm.district}
                          onChange={(e) => setBillingForm({ ...billingForm, district: e.target.value })}
                          placeholder="Beşiktaş"
                          className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-text-secondary text-sm mb-2">
                          {t('dashboard.postalCode')}
                        </label>
                        <input
                          type="text"
                          value={billingForm.postalCode}
                          onChange={(e) => setBillingForm({ ...billingForm, postalCode: e.target.value })}
                          placeholder="34000"
                          maxLength={5}
                          className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-text-secondary text-sm mb-2">
                        {t('dashboard.country')}
                      </label>
                      <input
                        type="text"
                        value={billingForm.country}
                        onChange={(e) => setBillingForm({ ...billingForm, country: e.target.value })}
                        placeholder="Türkiye"
                        className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSaveBilling}
                  disabled={savingBilling}
                  className="btn btn-primary mt-4"
                >
                  {savingBilling ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      {t('dashboard.saving')}
                    </>
                  ) : (
                    <>
                      <Receipt size={18} />
                      {t('dashboard.saveBillingInfo')}
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="card">
              <h3 className="font-semibold mb-4">{t('dashboard.notifications')}</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('dashboard.emailNotifications')}</div>
                    <div className="text-text-secondary text-sm">{t('dashboard.emailNotificationsDesc')}</div>
                  </div>
                  <button className="w-12 h-6 rounded-full bg-accent relative">
                    <div className="w-5 h-5 rounded-full bg-white absolute right-0.5 top-0.5" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{t('dashboard.projectUpdates')}</div>
                    <div className="text-text-secondary text-sm">{t('dashboard.projectUpdatesDesc')}</div>
                  </div>
                  <button className="w-12 h-6 rounded-full bg-accent relative">
                    <div className="w-5 h-5 rounded-full bg-white absolute right-0.5 top-0.5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="card border-error/20">
              <h3 className="font-semibold mb-4 text-error">{t('dashboard.dangerZone')}</h3>
              <p className="text-text-secondary text-sm mb-4">
                {t('dashboard.dangerZoneDesc')}
              </p>
              <button className="btn btn-ghost text-error border border-error/20 hover:bg-error/10">
                {t('dashboard.deleteAccount')}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-bg-base">
      <div className="container py-8 pb-32">
        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="space-y-6">
            {/* User Info */}
            <div className="card">
              <div className="flex items-center gap-4 mb-4">
                {user.photoURL && !imageError ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="w-14 h-14 rounded-full"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center text-accent text-xl font-bold">
                    {getInitials(user.displayName, user.email)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">
                    {user.displayName || t('dashboard.user')}
                  </div>
                  <div className="text-text-secondary text-xs truncate">{user.email}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className={cn('px-3 py-1.5 rounded-lg font-medium text-xs', getPlanBadge(userProfile?.plan || 'free'))}>
                  {(userProfile?.plan || 'free').charAt(0).toUpperCase() + (userProfile?.plan || 'free').slice(1)}
                </span>
                <span className="px-3 py-1.5 rounded-lg text-xs bg-bg-elevated border border-border-default text-text-tertiary">
                  {t('dashboard.memberSince')}{' '}
                  {userProfile?.createdAt
                    ? userProfile.createdAt.toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })
                    : t('dashboard.recently')}
                </span>
              </div>

              {billingForm.companyName && (
                <div className="flex items-center gap-2 mt-3 text-text-secondary text-sm">
                  <Building2 size={14} />
                  <span>{billingForm.companyName}</span>
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="card p-2">
              {tabs.map((tab) => (
                <Link
                  key={tab.id}
                  to={`/dashboard/${tab.id}`}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors',
                    activeTab === tab.id
                      ? 'bg-accent/20 text-accent'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                  )}
                >
                  <tab.icon size={18} />
                  {tab.label}
                  {tab.id === 'messages' && messages.filter(m => !m.read).length > 0 && (
                    <span className="ml-auto bg-accent text-white text-xs px-2 py-0.5 rounded-full">
                      {messages.filter(m => !m.read).length}
                    </span>
                  )}
                </Link>
              ))}

              <hr className="my-2 border-border-default" />

              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-error hover:bg-error/10 transition-colors"
              >
                <LogOut size={18} />
                {t('dashboard.signOut')}
              </button>
            </nav>

            {/* Contact Card */}
            <div className="card bg-gradient-to-br from-accent/10 to-purple-500/5 border-accent/20">
              <h4 className="font-semibold mb-2">{t('dashboard.needHelp')}</h4>
              <p className="text-text-secondary text-sm mb-4">{t('dashboard.needHelpDesc')}</p>
              <Link to="/contact" className="btn btn-primary btn-sm w-full">
                <Mail size={16} />
                {t('dashboard.contactSupport')}
              </Link>
            </div>
          </aside>

          {/* Main Content */}
          <main className="min-w-0 overflow-hidden">
            {/* Trial Status Banner */}
            {isTrialActive && daysRemaining <= 7 && (
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">
                        Deneme süreniz {daysRemaining} gün sonra sona erecek
                      </p>
                      <p className="text-sm text-gray-400">
                        Kesintisiz kullanıma devam etmek için planınızı seçin
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPaywall(true)}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
                  >
                    Planları Gör
                  </button>
                </div>
              </div>
            )}

            <h1 className="heading-lg mb-6">{tabs.find((t) => t.id === activeTab)?.label}</h1>
            {renderTabContent()}
          </main>
        </div>
      </div>

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        daysRemaining={daysRemaining}
        isTrialExpired={isTrialExpired}
        onSelectPlan={async (planId, billingCycle) => {
          try {
            const res = await fetch(`${FUNCTIONS_URL}/createCheckoutSession`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ plan: planId, billingCycle, userId: user?.uid }),
            });
            const data = await res.json();
            if (data.url) {
              window.location.href = data.url;
            } else {
              alert(data.error || 'Ödeme sayfası açılamadı.');
            }
          } catch {
            alert('Bağlantı hatası. Lütfen tekrar deneyin.');
          }
        }}
      />
    </div>
  );
}
